import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import UserPicker from '../../components/shared/UserPicker';
import ErrorBanner from '../../components/ui/ErrorBanner';
import { assignAsset } from '../../api/assets';
import { extractErrorMessage } from '../../api/client';

export default function AssignAssetModal({ open, asset, onClose }) {
  const [userId, setUserId] = useState('');
  const qc = useQueryClient();

  useEffect(() => {
    if (open) setUserId('');
  }, [open, asset?.id]);

  const mutation = useMutation({
    mutationFn: ({ id, uid }) => assignAsset(id, uid),
    onSuccess: () => {
      toast.success('Asset assigned');
      qc.invalidateQueries({ queryKey: ['assets'] });
      onClose?.();
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!userId) {
      toast.error('Please select a user');
      return;
    }
    mutation.mutate({ id: asset.id, uid: userId });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Assign Asset${asset?.name ? ` — ${asset.name}` : ''}`}
      description="Assign this asset to an active employee."
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={mutation.isPending}
          >
            Assign
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {mutation.isError && <ErrorBanner error={mutation.error} />}

        {asset && (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-ink-muted">
            <div>
              <span className="font-medium text-ink">Serial:</span>{' '}
              {asset.serialNumber || '—'}
            </div>
            <div>
              <span className="font-medium text-ink">Category:</span>{' '}
              {asset.category || '—'}
            </div>
          </div>
        )}

        <FormField label="Assign to" required>
          <UserPicker
            value={userId || ''}
            onChange={(v) => setUserId(v || '')}
            placeholder="Select an employee..."
          />
        </FormField>
      </form>
    </Modal>
  );
}
